/**
 * useMapTemplateBrowser Hook
 *
 * State management for the Map Templates browser modal.
 * Handles listing, creating, updating, applying, and deleting map templates.
 */

import { useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useEditorStore } from '@/stores/editor/editor-store';
import { mapTemplateApi } from '@/services/api/mapTemplateApi';
import type { MapTemplate } from '@/types/mapTemplate';
import type { Component } from '@/types';

// Fields to exclude from template configuration (transient/editor state)
const EXCLUDED_KEYS = new Set([
  'selectedPinId',
  'pinPlacementMode',
  'pendingPin',
  'pinDataBindingRequest',
  'data',
  'dataSource',
  'refreshInterval',
  'enableRealTime',
  'staleThreshold',
  'offlineThreshold',
  'groupId',
  'enablePerformanceMonitoring',
  'minVisibleSensors',
  'locationLocked',
  'onSensorClick',
  'dateRange',
  'linkedMapTemplateId',
]);

/**
 * Extract template-safe configuration from component properties
 */
function extractTemplateConfig(
  properties: Record<string, unknown>
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(properties).filter(([key]) => !EXCLUDED_KEYS.has(key))
  );
}

export type SortField = 'name' | 'updatedAt' | 'createdAt';

export function useMapTemplateBrowser(component: Component | null) {
  const { projectId } = useParams<{ projectId: string }>();
  const updateComponent = useEditorStore((s) => s.updateComponent);

  // Modal state
  const [isOpen, setIsOpen] = useState(false);
  const [templates, setTemplates] = useState<MapTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortField>('updatedAt');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Save As inline form state
  const [showSaveAsForm, setShowSaveAsForm] = useState(false);
  const [saveAsName, setSaveAsName] = useState('');
  const [saveAsDescription, setSaveAsDescription] = useState('');

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    if (!projectId) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await mapTemplateApi.listTemplates(projectId);
      setTemplates(result.templates);
    } catch (err) {
      // Silently fall back to empty list — the "No templates yet" empty state
      // is sufficient. Only surface errors for user-initiated write actions.
      setTemplates([]);
      console.error('[useMapTemplateBrowser] Failed to fetch templates:', err);
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  // Open modal and fetch templates
  const openModal = useCallback(() => {
    setIsOpen(true);
    setSelectedTemplateId(null);
    setShowSaveAsForm(false);
    setSaveAsName('');
    setSaveAsDescription('');
    fetchTemplates();
  }, [fetchTemplates]);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setError(null);
  }, []);

  // Filter and sort templates
  const filteredTemplates = useMemo(() => {
    let result = [...templates];

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          (t.description?.toLowerCase().includes(q))
      );
    }

    // Sort
    result.sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'updatedAt')
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

    return result;
  }, [templates, searchQuery, sortBy]);

  // The linked template ID from the current component
  const linkedTemplateId = component?.properties?.linkedMapTemplateId as string | undefined;

  // Save: update the linked template with current config
  const saveTemplate = useCallback(async () => {
    if (!component || !linkedTemplateId) return;
    setIsSaving(true);
    setError(null);
    try {
      const config = extractTemplateConfig(
        (component.properties as Record<string, unknown>) || {}
      );
      await mapTemplateApi.updateTemplate(linkedTemplateId, { configuration: config });
      await fetchTemplates();
    } catch (err) {
      setError('Failed to save template');
      console.error('[useMapTemplateBrowser] Failed to save template:', err);
    } finally {
      setIsSaving(false);
    }
  }, [component, linkedTemplateId, fetchTemplates]);

  // Save As: create a new template from current config
  const saveAsTemplate = useCallback(
    async (name: string, description?: string) => {
      if (!component || !projectId) return;
      setIsSaving(true);
      setError(null);
      try {
        const config = extractTemplateConfig(
          (component.properties as Record<string, unknown>) || {}
        );
        const newTemplate = await mapTemplateApi.createTemplate({
          name,
          description,
          projectId,
          configuration: config,
        });

        // Link the component to the new template
        updateComponent(component.id, {
          properties: {
            ...component.properties,
            linkedMapTemplateId: newTemplate._id,
          },
        });

        setSelectedTemplateId(newTemplate._id);
        setShowSaveAsForm(false);
        setSaveAsName('');
        setSaveAsDescription('');
        await fetchTemplates();
      } catch (err) {
        setError('Failed to create template');
        console.error('[useMapTemplateBrowser] Failed to create template:', err);
      } finally {
        setIsSaving(false);
      }
    },
    [component, projectId, updateComponent, fetchTemplates]
  );

  // Apply: load a template's config into the current component
  const applyTemplate = useCallback(
    async (templateId: string) => {
      if (!component) return;
      setIsSaving(true);
      setError(null);
      try {
        const template = await mapTemplateApi.getTemplateById(templateId);
        const currentProperties =
          (component.properties as Record<string, unknown>) || {};

        updateComponent(component.id, {
          properties: {
            ...currentProperties,
            ...template.configuration,
            linkedMapTemplateId: template._id,
          },
        });

        closeModal();
      } catch (err) {
        setError('Failed to apply template');
        console.error('[useMapTemplateBrowser] Failed to apply template:', err);
      } finally {
        setIsSaving(false);
      }
    },
    [component, updateComponent, closeModal]
  );

  // Delete: archive a template
  const deleteTemplate = useCallback(
    async (templateId: string) => {
      setError(null);
      try {
        await mapTemplateApi.deleteTemplate(templateId);
        if (selectedTemplateId === templateId) {
          setSelectedTemplateId(null);
        }
        // If the deleted template was linked, unlink it
        if (component && linkedTemplateId === templateId) {
          updateComponent(component.id, {
            properties: {
              ...component.properties,
              linkedMapTemplateId: undefined,
            },
          });
        }
        await fetchTemplates();
      } catch (err) {
        setError('Failed to delete template');
        console.error('[useMapTemplateBrowser] Failed to delete template:', err);
      }
    },
    [selectedTemplateId, component, linkedTemplateId, updateComponent, fetchTemplates]
  );

  // Rename: update template name
  const renameTemplate = useCallback(
    async (templateId: string, newName: string) => {
      setError(null);
      try {
        await mapTemplateApi.updateTemplate(templateId, { name: newName });
        await fetchTemplates();
      } catch (err) {
        setError('Failed to rename template');
        console.error('[useMapTemplateBrowser] Failed to rename template:', err);
      }
    },
    [fetchTemplates]
  );

  return {
    // Modal state
    isOpen,
    openModal,
    closeModal,

    // Data
    templates: filteredTemplates,
    isLoading,
    error,
    linkedTemplateId,

    // Search & sort
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,

    // Selection
    selectedTemplateId,
    setSelectedTemplateId,

    // Save As form
    showSaveAsForm,
    setShowSaveAsForm,
    saveAsName,
    setSaveAsName,
    saveAsDescription,
    setSaveAsDescription,

    // Actions
    saveTemplate,
    saveAsTemplate,
    applyTemplate,
    deleteTemplate,
    renameTemplate,
    isSaving,
  };
}
